"use client";
import {RefObject, useEffect, useRef, useState} from "react";

import {type LocationOption} from "@/features/locations";
import {type Payload, SimpleSearch, type SimpleSearchInstance} from "@/features/rental/simple-search/simple-search.model";

import {useServerRouter} from "@/shared/hooks/navigation/use-server-router";
import {useModel} from "@/shared/hooks/state-management/use-model";
import {useTranslation} from "@/shared/i18n";
import {DateHelper} from "@/shared/lib/helpers/date";

import {type SubmitVariant} from "./parts";

export type SearchSection = "where" | "when" | "who" | null;

export interface UseMobileSearchOptions {
    initialLocation?: LocationOption;
}

export interface UseMobileSearchReturn {
    // State
    search: SimpleSearchInstance;
    isOpen: boolean;
    activeSection: SearchSection;
    isSearchFocused: boolean;
    locationError: boolean;
    isSubmitting: boolean;
    containerRef: RefObject<HTMLDivElement | null>;

    // Computed
    isLastStep: boolean;
    submitLabel: string;
    submitVariant: SubmitVariant;

    // Handlers
    handleOpen: () => void;
    handleClose: () => void;
    toggleSection: (section: SearchSection) => void;
    handleSearchFocusChange: (isFocused: boolean) => void;
    handleLocationSelect: () => void;
    handleClear: () => void;
    handleNextStep: () => void;

    // Formatters
    getDateRangeValue: () => string | undefined;
    getGuestsValue: () => string | undefined;

    // Translation
    t: ReturnType<typeof useTranslation<"search">>["t"];
}

export function useMobileSearch(options?: UseMobileSearchOptions): UseMobileSearchReturn {
    const [search] = useModel<SimpleSearchInstance, [Payload?]>(SimpleSearch, {
        guests: {initialAdults: 1, minAdults: 1},
        location: options?.initialLocation,
    });
    const [isOpen, setIsOpen] = useState(false);
    const [activeSection, setActiveSection] = useState<SearchSection>(null);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [locationError, setLocationError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {t} = useTranslation("search");
    const containerRef = useRef<HTMLDivElement>(null);
    useServerRouter(search);

    // Handle z-index for parent element when drawer opens/closes
    useEffect(() => {
        const parentWithZIndex = containerRef.current?.closest('[data-search-z-index="search-z-index"]') as HTMLElement | null;
        if (parentWithZIndex) {
            if (isOpen) {
                parentWithZIndex.style.zIndex = "10000";
            } else {
                parentWithZIndex.style.zIndex = "";
            }
        }

        return () => {
            if (parentWithZIndex) {
                parentWithZIndex.style.zIndex = "";
            }
        };
    }, [isOpen]);

    const handleOpen = (): void => {
        setIsOpen(true);
        setActiveSection("where");
    };

    const handleClose = (): void => {
        setIsOpen(false);
        setIsSearchFocused(false);
    };

    const toggleSection = (section: SearchSection): void => {
        // Only switch to a different section, don't close the current one
        if (activeSection !== section) {
            setActiveSection(section);
            if (section !== "where") {
                setIsSearchFocused(false);
            }
        }
    };

    const handleSearchFocusChange = (isFocused: boolean): void => {
        setIsSearchFocused(isFocused);
    };

    const handleLocationSelect = (): void => {
        setLocationError(false);
        setActiveSection("when");
    };

    const handleClear = (): void => {
        search.locationSearch.clear();
        search.dateRange.clear();
        search.guests.reset();
    };

    const handleNextStep = (): void => {
        if (activeSection === "where") {
            setActiveSection("when");
        } else if (activeSection === "when") {
            setActiveSection("who");
        } else if (activeSection === "who") {
            // Validate location before submit
            if (!search.validate()) {
                setLocationError(true);
                setActiveSection("where");
                return;
            }
            // Last step - submit search
            setIsSubmitting(true);
            search.submit();
        }
    };

    // Computed values
    const isLastStep = activeSection === "who";
    const submitLabel = isLastStep ? t("searchButton") : t("next");
    const submitVariant: SubmitVariant = isLastStep ? "primary" : "secondary-outline";

    // Format dates for display
    const getDateRangeValue = (): string | undefined => {
        return DateHelper.formatDateRangeCompact(search.dateRange.startDate, search.dateRange.endDate);
    };

    // Format guests for display
    const getGuestsValue = (): string | undefined => {
        const total = search.guests.totalGuests;
        const pets = search.guests.totalPets;

        // Return undefined if default state (0 guests, 0 pets) - show "Add guests" text
        if (total === 0 && pets === 0) return undefined;

        const parts: Array<string> = [];
        if (total > 0) {
            parts.push(t("guestsCount", {count: total}));
        }
        if (pets > 0) {
            parts.push(t("dogsCount", {count: pets}));
        }

        return parts.join(", ") || undefined;
    };

    return {
        // State
        search,
        isOpen,
        activeSection,
        isSearchFocused,
        locationError,
        isSubmitting,
        containerRef,

        // Computed
        isLastStep,
        submitLabel,
        submitVariant,

        // Handlers
        handleOpen,
        handleClose,
        toggleSection,
        handleSearchFocusChange,
        handleLocationSelect,
        handleClear,
        handleNextStep,

        // Formatters
        getDateRangeValue,
        getGuestsValue,

        // Translation
        t,
    };
}
